/**
 * Mirrors pkg/go/rbac/permissions.go's AllPermissions. Keep both in sync —
 * this list is UI-only (there's no ListPermissions RPC); the backend is the
 * source of truth and validates every permission string it receives.
 *
 * There is no "view:*" entry: viewing is never gated by a permission at all
 * — any authenticated tenant member can view any tenant resource, full
 * stop. access_rules narrow which specific deployments/workspaces are
 * reachable, but that's a resource-reachability check with no permission
 * string involved.
 */

export const PERMISSIONS = {
  createPartner: 'create:partner',
  editPartner: 'edit:partner',
  deletePartner: 'delete:partner',

  createTenant: 'create:tenant',
  editTenant: 'edit:tenant',
  deleteTenant: 'delete:tenant',

  createDeployment: 'create:deployment',
  editDeployment: 'edit:deployment',
  deleteDeployment: 'delete:deployment',

  createWorkspace: 'create:workspace',
  editWorkspace: 'edit:workspace',
  deleteWorkspace: 'delete:workspace',
  promoteWorkspace: 'promote:workspace',
  upgradeWorkspace: 'upgrade:workspace',
  deploySingleServiceWorkspace: 'deploy_single_service:workspace',
  deployCsaiWorkspace: 'deploy_csai:workspace',
  deployMaiWorkspace: 'deploy_mai:workspace',

  editRelease: 'edit:release',
  syncRelease: 'sync:release',
  deprecateRelease: 'deprecate:release',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/** Resource kind a permission applies to — the part after the colon. */
export function resourceKindOf(permission: string): string {
  return permission.split(':')[1] ?? '';
}

/** Every resource kind a permission can name — mirrors pkg/go/rbac's ResourceKinds. */
export const RESOURCE_KINDS = ['partner', 'tenant', 'deployment', 'workspace', 'release'] as const;

export const RESOURCE_KIND_LABELS: Record<string, string> = {
  partner: 'Partner',
  tenant: 'Customer',
  deployment: 'Deployment',
  workspace: 'Workspace',
  release: 'Release',
};

/** Actions rendered as full acronyms rather than title-cased words. */
const ACTION_ACRONYMS = new Set(['csai', 'mai']);

/** Turns an action string like "deploy_single_service" into "Deploy Single Service". */
function formatAction(action: string): string {
  return action
    .split('_')
    .map((word) =>
      ACTION_ACRONYMS.has(word)
        ? word.toUpperCase()
        : `${word.charAt(0).toUpperCase()}${word.slice(1)}`,
    )
    .join(' ');
}

/** Human label for a permission, e.g. "view:workspace" -> "View workspace". */
export function permissionLabel(permission: string): string {
  const [action, kind] = permission.split(':');
  if (!action || !kind) return permission;
  const kindLabel = RESOURCE_KIND_LABELS[kind] ?? kind;
  return `${formatAction(action)} ${kindLabel.toLowerCase()}`;
}

/**
 * The action half of a permission only, e.g. "create:workspace" -> "Create"
 * — for a checkbox already grouped under a resource-kind header (that header
 * already says "Workspace"; repeating it on every checkbox is redundant).
 */
export function permissionActionLabel(permission: string): string {
  const [action] = permission.split(':');
  if (!action) return permission;
  return formatAction(action);
}

/**
 * Permissions grouped by resource kind, in a fixed display order. Used by
 * the role permission picker — action permissions only, since a role can
 * never hold a view permission.
 */
export const PERMISSIONS_BY_RESOURCE_KIND: {
  kind: string;
  label: string;
  permissions: Permission[];
}[] = RESOURCE_KINDS.map((kind) => ({
  kind,
  label: RESOURCE_KIND_LABELS[kind] ?? kind,
  permissions: ALL_PERMISSIONS.filter((p) => resourceKindOf(p) === kind),
}));

/**
 * Mirrors pkg/go/rbac's AllRestrictableAttributes — the flat, code-owned
 * allow-list of resource attributes a rule_based access_rules restriction
 * may reference.
 */
export const ALL_RESTRICTABLE_ATTRIBUTES = ['environment', 'region'] as const;

/**
 * Which attribute a rule_based condition offers for each resource kind
 * (SUN-408 round 2 hierarchy — the Deployments/Workspaces sections in the
 * access-rule builder are now each explicitly scoped to one kind, so the
 * attribute picker no longer needs to be kind-agnostic). Mirrors the actual
 * call-site Attrs population in workspace_service.go/deployment_service.go,
 * not RestrictableAttributes' own nominal (and more permissive) validation
 * allow-list — a workspace Resource never carries "region" in Attrs, so a
 * region condition could never match one anyway; see
 * internal/management/services/v1/accessrule/evaluate.go's
 * evaluateRuleCondition.
 */
export const ATTRIBUTES_BY_RESOURCE_KIND: Record<'deployment' | 'workspace', string[]> = {
  deployment: ['region'],
  workspace: ['environment'],
};

/**
 * Mirrors pkg/go/rbac's RuleConditionOps — the only Op values the backend
 * interpreter (accessrule.evaluateRuleCondition) actually knows how to
 * apply. "eq" is the only one today.
 */
export const RULE_CONDITION_OPS = ['eq'] as const;
